import React, { useRef, useEffect } from "react";
import { motion, useAnimation, useInView, Variants } from "framer-motion";
import type { JSX } from "react";
import styles from "./AnimatedTitle.module.css";

type Fragment = { text: string; className?: string };
type AnimatedTitleText = Fragment[] | string;

interface AnimatedTitleProps {
  text: AnimatedTitleText[] | AnimatedTitleText;
  as?: keyof JSX.IntrinsicElements;
  className?: string;
  delayPerChar?: number;
  delayPerLine?: number;
  initialY?: string | number;
  initialRotate?: number;
  splitBy?: string | RegExp;
}

const letterVariants: Variants = {
  initial: (custom: { y: string | number; rotate: number }) => ({
    y: custom.y,
    rotate: custom.rotate,
    opacity: 0,
  }),
  animate: (custom: { y: string | number; rotate: number }) => ({
    y: 0,
    rotate: 0,
    opacity: 1,
    transition: {
      type: "spring" as const,
      damping: 20,
      stiffness: 100,
    },
  }),
};

export const AnimatedTitle: React.FC<AnimatedTitleProps> = ({
  text,
  as = "h1",
  className = "",
  delayPerChar = 0.04,
  delayPerLine = 0.05,
  initialY = "100%",
  initialRotate = 2,
  splitBy,
}) => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { amount: 0.06, once: true });
  const animationControls = useAnimation();

  useEffect(() => {
    if (isInView) {
      animationControls.start("animate");
    }
  }, [isInView, animationControls]);

  const MotionTag = motion[as as keyof typeof motion] as typeof motion.div;

  // Допоміжна функція для рендеру тексту (масив фрагментів або рядок)
  function renderText(text: AnimatedTitleText): React.ReactNode {
    if (Array.isArray(text)) {
      return text.map((fragment, index) => {
        if (typeof fragment === "string") {
          return renderText(fragment);
        }
        const words = fragment.text.split(" ");
        return (
          <span key={index} className={fragment.className || ""}>
            {words.map((word, wIdx) => (
              <React.Fragment key={wIdx}>
                <span className={styles.word}>
                  {word.split("").map((char, cIdx) => (
                    <motion.span
                      key={cIdx}
                      custom={{ y: initialY, rotate: initialRotate }}
                      variants={letterVariants}
                      style={{ display: "inline-block" }}
                    >
                      {char}
                    </motion.span>
                  ))}
                </span>
                {wIdx !== words.length - 1 && <span className={styles.space}> </span>}
              </React.Fragment>
            ))}
          </span>
        );
      });
    } else {
      // text — це простий рядок
      return text.split(" ").map((word, wIdx, arr) => (
        <React.Fragment key={wIdx}>
          <span className={styles.word}>
            {word.split("").map((char, cIdx) => (
              <motion.span
                key={cIdx}
                custom={{ y: initialY, rotate: initialRotate }}
                variants={letterVariants}
                style={{ display: "inline-block" }}
              >
                {char}
              </motion.span>
            ))}
          </span>
          {wIdx !== arr.length - 1 && <span className={styles.space}> </span>}
        </React.Fragment>
      ));
    }
  }

  return (
    <MotionTag
      ref={containerRef}
      className={`${styles.title} ${className}`}
      initial="initial"
      animate={animationControls}
      variants={{
        animate: {
          transition: {
            staggerChildren: delayPerLine,
          },
        },
      }}
    >
      {Array.isArray(text)
        ? text.map((t, idx) => (
            <React.Fragment key={idx}>
              {Array.isArray(t)
                ? renderText(t as Fragment[])
                : typeof t === 'string'
                  ? renderText(t as string)
                  : renderText([t as Fragment])
              }
            </React.Fragment>
          ))
        : renderText(text)
      }
    </MotionTag>
  );
};

export default AnimatedTitle;
